/**
 * Where the PageStitch Agent proxy lives, and whether this page can get there.
 *
 * Split out of `agent-api.js` and deliberately dependency-free: the command bar
 * and the phone sheet need the reachability answer at BOOT, to decide whether
 * to render a trigger at all, and neither should drag the agent transport into
 * the static module graph to ask a two-line question.
 */

/**
 * `__PB_AGENT_ENDPOINT` comes from the deploy-config block in `index.html`
 * (`agentEndpoint`), which leaves it empty on loopback AND empty in production
 * until a hosted proxy actually exists. Absent or empty, the proxy is assumed
 * to be the local one on its conventional port — which is why an https page
 * with no hosted proxy configured reports unreachable below.
 *
 * (This used to say the dev server injected it. Nothing did: no code outside
 * the tests ever wrote the global, so the configured branch was dead until the
 * deploy-config entry was added.)
 */
export function getAgentOrigin(win = globalThis.window) {
  const configured =
    typeof win?.__PB_AGENT_ENDPOINT === 'string' ? win.__PB_AGENT_ENDPOINT.trim() : '';
  return new URL(configured || 'http://localhost:3001').origin;
}

/**
 * Can a request from THIS page reach the proxy at all?
 *
 * Because the endpoint global is dev-server-only, a deployed page resolves the
 * `http://localhost:3001` default — which a secure page may not load. The
 * browser blocks it before the request leaves the tab, so every call on
 * production was a guaranteed failure, logged three times per status refresh as
 * mixed content, behind a button that could never work.
 *
 * This is a REACHABILITY test, not an is-production test: a dev machine served
 * over https with an https endpoint configured stays reachable, and a plain
 * http page keeps working everywhere it did before.
 */
export function isAgentReachable(win = globalThis.window) {
  if (win?.location?.protocol !== 'https:') return true;
  return new URL(getAgentOrigin(win)).protocol === 'https:';
}
